import { Dispatch, type FC, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PaidServiceOption } from '@/__generated_graphql__/types';
import { CARD_IDS } from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.types';
import { ModalOptionCard } from '@/components/AdvertisePayments/components/Options/OptionCard';
import { TariffCard } from '@/components/TariffCard/TariffCard';
import { ArrowUpIcon, PremiumIcon } from '@/components/ui/Icons/Icons';
import { PaymentTypeEnum } from '@/constants/common';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import * as S from './Options.styles';
import { OptionItem } from './Options.types';

export type OptionsModalProps = {
  activeCard: string;
  setActiveCard: Dispatch<SetStateAction<string>>;
  items: PaidServiceOption[];
  type: PaymentTypeEnum;
};

export const OptionsModal: FC<OptionsModalProps> = ({
  activeCard,
  setActiveCard,
  type = PaymentTypeEnum.ADVERTISE,
  items
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('mobile');
  const isTablet = useMediaQuery('tablet');

  const { ADVERTISE, PUBLICATION } = PaymentTypeEnum;

  const advertiseCards: OptionItem[] = useMemo(
    () => [
      {
        id: CARD_IDS.TOP,
        icon: <ArrowUpIcon />,
        title: t('Advertise.btn_upgrade_top'),
        subtitle: t('Advertise.price_from_3_azn')
      },
      {
        id: CARD_IDS.PREMIUM,
        icon: <PremiumIcon />,
        title: t('Advertise.btn_upgrade_premium'),
        subtitle: t('Advertise.price_from_10_azn')
      }
    ],
    [isMobile, isTablet, t]
  );

  return (
    <S.OptionsSection>
      <S.OptionsCardRow $isPublicationPayment={type === PUBLICATION}>
        {type === PUBLICATION &&
          items.map(
            item =>
              item &&
              'description' in item && (
                <TariffCard
                  key={item.id}
                  title={item.name}
                  description={item.description ?? ''}
                  price={item.price}
                  isSelected={activeCard === item.id}
                  onClick={() => setActiveCard(item.id)}
                />
              )
          )}

        {type === ADVERTISE &&
          advertiseCards.map(({ id, icon, title, subtitle }) => (
            <ModalOptionCard
              key={id}
              icon={icon}
              title={title}
              subtitle={subtitle}
              isActive={activeCard === id}
              cardId={id}
              onClick={() => setActiveCard(id)}
            />
          ))}
      </S.OptionsCardRow>
    </S.OptionsSection>
  );
};
